import { ArticleType, SubscriberArticlePage, CommandResult, SubscriberArticle, SimpleArticleList, SubscriberArticleSort, ArticleChangeImportance, PublicAmorgUser, InternalSubscriberArticleListing, InternalSubscriberArticleView, InternalArticleFieldChange, ArticleListOverview, PublicArticle } from "../amorg-common";
import { apiPath } from "./../amorg-common";
import { commandAuthWrapper, queryAuthWrapper } from "./serverApiHelpers";

const articlesPath = `${apiPath}/articles`;


export async function getArticleTypes (): Promise<ArticleType[]>
{
    const data = await queryAuthWrapper(`${articlesPath}/types`);
    return data;

}

export async function getArticleListTopics (): Promise<SimpleArticleList[]>
{
    const data = await queryAuthWrapper(`${articlesPath}/topics`);
    return data;

}

export const getArticleListOverview = async (): Promise<ArticleListOverview | null> =>
{
    const data = await queryAuthWrapper(`${articlesPath}`) as ArticleListOverview | null;
    return data;
};

export async function getArticleListing (listId?: string, topicId?: string, typeId?: string, ownerUserId?: string, pageNumber?:number, searchTerm?: string, sort?:{sortBy?:SubscriberArticleSort, ascending?:boolean}): Promise<SubscriberArticlePage>
{
    const queryParams: string[] = [];

    if(listId)
        queryParams.push(`listId=${listId}`);
    if (topicId)
        queryParams.push(`topic=${topicId}`);
    if (typeId)
        queryParams.push(`type=${typeId}`);
    if (ownerUserId)
        queryParams.push(`owner=${ownerUserId}`);
    if (pageNumber)
        queryParams.push(`pageNumber=${pageNumber}`);
    if (searchTerm)
        queryParams.push(`search=${searchTerm}`);
    
    if(sort){
        queryParams.push(`sort=${sort.sortBy}`);
        if(sort.ascending)
            queryParams.push(`order=asc`);
        else
            queryParams.push(`order=desc`);
    }

    const query = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    const data = await queryAuthWrapper(`${articlesPath}/lists${query}`) as SubscriberArticlePage;
    for(const a of data.articles)
    {
        a.date = a.date ? new Date(a.date) : null;
        if(a.release)
            a.release.releasedDate = new Date(a.release.releasedDate);
        a.evaluationLastEdited = a.evaluationLastEdited 
            ? new Date(a.evaluationLastEdited)
            : null;
    }
    return data;

}

export async function getArticle (articleId: string): Promise<SubscriberArticle[]>
{
    const data = await queryAuthWrapper(`${articlesPath}/${articleId}`) as SubscriberArticle[];
    for(const a of data)
    {
        if(a.date)
            a.date = new Date(a.date);

        if(a.release)
            a.release.releasedDate = new Date(a.release.releasedDate);

        if(a.evaluation)
            a.evaluation.lastEdited.date = new Date(a.evaluation.lastEdited.date);
        
        if(a.customRequirements)
            a.customRequirements.lastEdited.date = new Date(a.customRequirements.lastEdited.date);

        for(const app of a.appendices)
            if(app.date)
                app.date = new Date(app.date);

        for(const c of a.versionComments)
        {
            c.created = new Date(c.created);
            if(c.updated)
                c.updated = new Date(c.updated);
        }
    }

    return data;
}

export async function getPublicArticle (articleId: string, siteId: string): Promise<PublicArticle[] | null>
{
    const data = await queryAuthWrapper(`${articlesPath}/public/${siteId}/${articleId}`, true) as PublicArticle[] | null;
    if (!data)
        return null;

    for (const a of data)
    {
        if (a.date)
            a.date = new Date(a.date);

        if (a.release)
            a.release.releasedDate = new Date(a.release.releasedDate);

        if (a.evaluation)
            a.evaluation.lastEdited.date = new Date(a.evaluation.lastEdited.date);

        if (a.customRequirements)
            a.customRequirements.lastEdited.date = new Date(a.customRequirements.lastEdited.date);

        for (const app of a.appendices)
            if (app.date)
                app.date = new Date(app.date);
    }

    return data;
}

export async function createArticleEvaluation (articleVersionId:string): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/evaluations`, 'POST',
        {
            articleVersionId, evaluationImportance:null, evaluationText:'Ny evaluering'
        }
    );
}

export async function updateArticleEvaluation (articleVersionId: string, evaluationText: string, evaluationImportance: ArticleChangeImportance | null): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/evaluations/${articleVersionId}`, 'PUT',
        {
            evaluationText, evaluationImportance
        }
    );
}

export async function setCustomRequirements (articleId: string, requirementsText: string): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/requirements/${articleId}`, 'PUT',
        {
            requirementsText
        }
    );
}

export async function assignArticleOwner(articleId: string, ownerUserId:string|null): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/owner/${articleId}`, 'PUT',
        {
            ownerUserId
        }
    );
}

export async function setArticleCategoryTag (articleId: string, categoryTag: string): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/categoryTag/${articleId}`, 'PUT',
        {
            categoryTag
        }
    );
}

export async function getArticleCategoryTags (): Promise<string[]>
{
    const data = await queryAuthWrapper(`${articlesPath}/categoryTags`);
    return data;
}

export async function getCurrentArticleOwners(): Promise<PublicAmorgUser[]>
{
    const data: PublicAmorgUser[] = await queryAuthWrapper(`${articlesPath}/currentOwners`);
    return data.map(u => ({
        ...u,
        created: new Date(u.created),
        currentRole: 
            u.currentRole
                ?   {
                        ...u.currentRole,
                        start: new Date(u.currentRole.start),
                        end: new Date(u.currentRole.end)
                    }
                : null
    }));
}

export async function getInternalArticle(internalArticleId:string):Promise<InternalSubscriberArticleView|null>
{
    const data:InternalSubscriberArticleView = await queryAuthWrapper(`${articlesPath}/internal/${internalArticleId}`);
    if(!data)
        return null;
    
    return {
        ...data,
        date: data.date ? new Date(data.date) : null,
        lastEdited:{
            ...data.lastEdited,
            date:new Date(data.lastEdited.date)
        },
        lastVersionEdited:{
            ...data.lastVersionEdited,
            date:new Date(data.lastVersionEdited.date)
        },
        previousVersions:data.previousVersions.map(c => ({
            ...c,
            date: c.date ? new Date(c.date) : null,
            lastEdited:{
                ...c.lastEdited,
                date:new Date(c.lastEdited.date)
            }
        }))
    }
}

export async function getInternalArticles(includeDeleted = false): Promise<InternalSubscriberArticleListing[]>
{
    const data: InternalSubscriberArticleListing[] = await queryAuthWrapper(`${articlesPath}/internal${includeDeleted ? '?includeDeleted=1' : ''}`);
    return data.map(a => ({
        ...a,
        date: a.date ? new Date(a.date) : null,
    }));
}

export async function setInternalArticleDeleted(internalArticleId: string, isDeleted: boolean): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/internal/${internalArticleId}/deleted`, 'PUT', { isDeleted });
}

export async function createInternalArticle(
    internalArticleId:string,
    title:string, 
    description:string, 
    ownerUserId:string|null, 
    changeId:string,
    changeDescription:string,
    date:Date, 
    evaluationImportance:ArticleChangeImportance|null, 
    evaluationText:string
): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/internal`, 'POST',
        {
            internalArticleId,
            title,
            description,
            ownerUserId,
            changeId,
            changeDescription,
            date,
            evaluationImportance,
            evaluationText
        }
    );
}

export async function createInternalArticleChange(
    internalArticleId:string,
    changeId:string
): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/internal/${internalArticleId}`, 'POST',
        {
            changeId
        }
    );
}

export async function updateInternalArticle(
    internalArticleId:string,
    change:InternalArticleFieldChange
): Promise<CommandResult>
{
    return await commandAuthWrapper(`${articlesPath}/internal/${internalArticleId}`, 'PUT',
        {
            change
        }
    );
}

export const downloadArticlesLight = `${articlesPath}/listdownload`;
export const downloadArticlesAudit = `${articlesPath}/listdownload?audit=1`;